import React from "react";
import { motion } from "framer-motion";
import {
  BadgeCheck,
  Wallet,
  PackageSearch,
  Headphones,
  Truck,
} from "lucide-react";

const features = [
  {
    Icon: BadgeCheck,
    title: "Genuine Products",
    desc: "100% authentic, warranty-backed products sourced directly from authorized channels.",
    span: "lg:col-span-3",
    highlight: true,
  },
  {
    Icon: Wallet,
    title: "Competitive Prices",
    desc: "Best wholesale rates for bulk buyers, contractors, retailers & individual customers alike.",
    span: "lg:col-span-3",
  },
  {
    Icon: PackageSearch,
    title: "Wide Product Range",
    desc: "500+ SKUs across 8 categories.",
    span: "lg:col-span-2",
  },
  {
    Icon: Headphones,
    title: "Expert Guidance",
    desc: "Trained staff to help you choose the right product for every project.",
    span: "lg:col-span-2",
  },
  {
    Icon: Truck,
    title: "Fast Service",
    desc: "Quick billing, ready stock and prompt delivery across Nashik.",
    span: "lg:col-span-2",
  },
];

export default function WhyChooseUs() {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="py-16 md:py-24 bg-[#00296B] relative overflow-hidden"
    >
      {/* subtle bg pattern */}
      <div className="absolute inset-0 opacity-[0.06]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #FFD500 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFD500] font-semibold mb-4">
            — Why Choose Us
          </div>
          <h2
            className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight"
            data-testid="why-us-heading"
          >
            Five reasons Nashik trusts{" "}
            <span className="text-[#FFD500]">Master Mall</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/70 max-w-2xl">
            More than a showroom — we&apos;re a reliable partner for every electrical
            requirement, big or small.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              data-testid={`why-us-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`${f.span} ${
                f.highlight
                  ? "bg-[#FFD500] text-[#00296B]"
                  : "bg-white/[0.04] backdrop-blur-sm border border-white/10 text-white"
              } p-8 min-h-[220px] flex flex-col justify-between group hover:border-[#FFD500]/50 transition-all`}
            >
              <div
                className={`w-12 h-12 flex items-center justify-center ${
                  f.highlight ? "bg-[#00296B]" : "bg-[#FFD500]"
                }`}
              >
                <f.Icon
                  className={`w-6 h-6 ${
                    f.highlight ? "text-[#FFD500]" : "text-[#00296B]"
                  }`}
                  strokeWidth={1.8}
                />
              </div>
              <div>
                <h3
                  className={`font-display font-semibold text-xl sm:text-2xl leading-tight ${
                    f.highlight ? "text-[#00296B]" : "text-white"
                  }`}
                >
                  {f.title}
                </h3>
                <p
                  className={`mt-3 text-sm leading-relaxed ${
                    f.highlight ? "text-[#00296B]/85" : "text-white/70"
                  }`}
                >
                  {f.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
